<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ScenariosAPI from 'dashboard/api/scenarios';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { conditionMessages } from './conditions';

const props = defineProps({
  field: { type: Object, required: true },
  context: { type: String, required: true },
  connectionId: { type: Number, default: null },
});
const rule = defineModel('rule', { type: Object, required: true });
const { t } = useI18n({ useScope: 'local', messages: conditionMessages });
const listId = `condition-values-${getCurrentInstance().uid}`;
const open = ref(false);
const query = ref('');
const options = ref([]);
const nextOffset = ref(null);
const busy = ref(false);
const error = ref(false);
const resolved = ref('');
let revision = 0;
let timer;
const reference = computed(() => !!props.field.references);
const picker = computed(
  () =>
    reference.value ||
    (props.field.choices && ['eq', 'neq'].includes(rule.value.op))
);
const selectedLabel = computed(
  () =>
    (!reference.value && rule.value.value) ||
    resolved.value ||
    rule.value.value_label ||
    t('conditionEditor.unknownValue')
);
const load = async (append = false) => {
  if (!props.connectionId) return;
  revision += 1;
  const version = revision;
  busy.value = true;
  error.value = false;
  try {
    const { data } = await ScenariosAPI.conditionValues({
      connection_id: props.connectionId,
      context: props.context,
      field: rule.value.field,
      query: picker.value ? query.value : rule.value.value || '',
      offset: append ? nextOffset.value : 0,
    });
    if (version !== revision) return;
    options.value = append ? [...options.value, ...data.options] : data.options;
    nextOffset.value = data.next_offset;
  } catch {
    if (version === revision) error.value = true;
  } finally {
    if (version === revision) busy.value = false;
  }
};
const resolveSelected = async () => {
  if (!reference.value || !props.connectionId || rule.value.value == null)
    return;
  const version = revision;
  try {
    const { data } = await ScenariosAPI.conditionValues({
      connection_id: props.connectionId,
      context: props.context,
      field: rule.value.field,
      value: rule.value.value,
      mis_number: rule.value.reference_mis_number,
    });
    if (version === revision && data.options.length === 1)
      resolved.value = data.options[0].label;
  } catch {
    // The stored rule remains intact; opening the picker offers an explicit retry.
    if (version === revision) error.value = true;
  }
};
const select = option => {
  rule.value.value = option.value;
  rule.value.reference_mis_number = option.mis_number;
  rule.value.value_label = option.label;
  resolved.value = option.label;
  open.value = false;
};
const toggle = () => {
  open.value = !open.value;
  if (open.value) load();
};
watch(
  () => [props.connectionId, rule.value.field, rule.value.op, props.context],
  () => {
    clearTimeout(timer);
    revision += 1;
    open.value = false;
    busy.value = false;
    options.value = [];
    query.value = '';
    resolved.value = '';
    error.value = false;
    resolveSelected();
  },
  { immediate: true }
);
watch([query, () => rule.value.value], () => {
  clearTimeout(timer);
  if (!picker.value || open.value) {
    revision += 1;
    options.value = [];
    timer = setTimeout(() => load(), 250);
  }
});
onBeforeUnmount(() => {
  clearTimeout(timer);
  revision += 1;
});
</script>

<template>
  <div class="min-w-0 space-y-2">
    <template v-if="picker">
      <span class="block text-xs text-n-slate-11">{{
        t('conditionEditor.value')
      }}</span>
      <Button
        type="button"
        color="slate"
        variant="outline"
        class="w-full justify-between text-start"
        :label="
          rule.value == null ? t('conditionEditor.chooseValue') : selectedLabel
        "
        :aria-expanded="open"
        :aria-controls="listId"
        icon="i-lucide-chevron-down"
        trailing-icon
        :disabled="!connectionId"
        @click="toggle"
      />
      <div
        v-if="open"
        :id="listId"
        class="border border-n-weak rounded-lg p-2 space-y-2"
        @keydown.esc="open = false"
      >
        <Input
          v-model="query"
          type="search"
          :placeholder="t('conditionEditor.searchValues')"
          :aria-label="t('conditionEditor.searchValues')"
        />
        <div class="max-h-48 overflow-y-auto overscroll-contain">
          <button
            v-for="(option, index) in options"
            :key="`${option.mis_number}:${option.value}:${index}`"
            type="button"
            class="block w-full rounded-md px-3 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="select(option)"
          >
            {{ option.label }}
            <span
              v-if="
                reference &&
                new Set(options.map(item => item.mis_number)).size > 1
              "
              class="block text-xs text-n-slate-11"
            >
              {{ t('conditionEditor.mis', { number: option.mis_number }) }}
            </span>
          </button>
        </div>
        <p
          v-if="!busy && !error && !options.length"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('conditionEditor.noValues') }}
        </p>
        <Button
          v-if="nextOffset !== null && !error"
          type="button"
          size="sm"
          variant="ghost"
          :label="t('conditionEditor.more')"
          :disabled="busy"
          @click="load(true)"
        />
      </div>
    </template>
    <template v-else>
      <Input
        v-model="rule.value"
        :label="t('conditionEditor.value')"
        :list="listId"
        @focus="load()"
      />
      <datalist :id="listId">
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </datalist>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('conditionEditor.textHint') }}
      </p>
    </template>
    <p v-if="busy" class="m-0 text-xs text-n-slate-11" role="status">
      {{ t('conditionEditor.loadingValues') }}
    </p>
    <div v-if="error" class="text-xs text-n-ruby-11" role="status">
      {{ t('conditionEditor.valuesError') }}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        :label="t('conditionEditor.retry')"
        @click="load()"
      />
    </div>
    <p v-if="!connectionId" class="m-0 text-xs text-n-amber-11">
      {{ t('conditionEditor.connectionRequired') }}
    </p>
  </div>
</template>
